import React, { Component } from "react";
import { BrowserRouter as Router, Navigate } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import BaseRouter from "./routes";
import * as authActions from "./store/actions/auth";
import * as notificationActions from "./store/actions/notification";

// Global axios error interceptor - logs network/server errors
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      const status = error.response.status;
      if (status === 401) {
        console.warn("Unauthorized - token may be expired");
      } else if (status >= 500) {
        console.error("Server error:", error.response.data);
      }
    } else if (error.request) {
      console.error("Network error - no response received");
    }
    return Promise.reject(error);
  }
);

import { getPermissions } from "./store/actions/permission";
import { getRoles } from "./store/actions/user_role";
import { getUsers, getCompanies, getGroups } from './store/actions/users';
import { getAssets } from './store/actions/assets';
import { getAlarms } from "./store/actions/alarms";
import { getVPNs } from './store/actions/vpns';
import { getSystems } from './store/actions/systems';
import { getLocations, getSubLocations } from './store/actions/locations';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from "./components/Alert";
import * as actionTypes from "./store/actions/actionTypes";

import "semantic-ui-css/semantic.min.css";
import CustomLayout from "./containers/Layout";

import 'react-svg-radar-chart/build/css/index.css'
import 'mapbox-gl/dist/mapbox-gl.css';
import "./style.scss"

class App extends Component {

  dataLoaded = false;

  componentDidMount() {
    const { onTryAutoSignup } = this.props
    onTryAutoSignup();
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    // Only reload data when auth state changes (login/logout)
    if (this.props.isAuthenticated !== prevProps.isAuthenticated) {
      this.dataLoaded = false;
      this.loadData();
    }
  }

  loadData() {
    if (this.dataLoaded) return;

    const { getPermissionList, getRoleList, getCompanyList, getGroupList, getUserList,
      getAssetList, getVPNList, getLocationList, getSubLocationList, getSystemList, getUserAuth, getAlarmList } = this.props
    const token = localStorage.getItem("token")

    if (token) {
      this.props.setLoading(true);
      getUserAuth(token)

      // Essential data - needed for navigation and auth
      getPermissionList()
      getRoleList()
      getCompanyList()
      getGroupList()

      // Core data - needed for dashboard and most pages
      getAssetList()
      getLocationList()
      getSubLocationList()
      getSystemList()

      // Secondary data - load in background
      getUserList()
      getAlarmList()
      getVPNList()
    } else {
      // Public data only
      getCompanyList()
      getGroupList()
    }

    this.dataLoaded = true;
  }

  render() {

    const { isAuthenticated, closeSuccessSB, closeErrorSB, closeInfoSB, successData, errorData, infoData, dashboardLoading } = this.props
    const unSignedPages = [
      "/login", "/logout", "/signup", "/register-complete"
      , "/forgot-pwd", "/reset-pwd", "/verify-otp", '/signup-success',
      "/log/verify-otp"
    ]
    const sharedPage = [
      '/terms-condition', '/privacy-policy'
    ]
    if (!isAuthenticated) sharedPage.map(page => { return unSignedPages.push(page) })
    const isSignPage = unSignedPages.includes(window.location.pathname)

    return (
      <Router>
        <CustomLayout {...this.props}>
          {dashboardLoading && (
            <div className="global-loading-overlay">
              <CircularProgress size={48} style={{ color: '#F7931E' }} />
            </div>
          )}
          <BaseRouter />

          {
            (!isAuthenticated) && (!isSignPage) && <Navigate to="/login" />
          }
          {
            (isAuthenticated) && (isSignPage) && <Navigate to="/dashboard?from=login" />
          }

          <Snackbar open={successData.open} autoHideDuration={6000} onClose={closeSuccessSB}>
            <Alert onClose={closeSuccessSB} severity="success" sx={{ width: '100%' }}>
              {successData.content}
            </Alert>
          </Snackbar>

          <Snackbar open={errorData.open} autoHideDuration={6000} onClose={closeErrorSB}>
            <Alert onClose={closeErrorSB} severity="error" sx={{ width: '100%' }}>
              {errorData.content}
            </Alert>
          </Snackbar>

          <Snackbar open={infoData.open} autoHideDuration={6000} onClose={closeInfoSB}>
            <Alert onClose={closeInfoSB} severity="info" sx={{ width: '100%' }}>
              {infoData.content}
            </Alert>
          </Snackbar>
        </CustomLayout>
      </Router>

    );
  }
}

const mapStateToProps = state => {
  return {
    isAuthenticated: state.auth.token !== null,
    successData: state.notification.success,
    errorData: state.notification.error,
    infoData: state.notification.info,
    authUser: state.auth.user,
    dashboardLoading: state.dashboard.loading,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onTryAutoSignup: () => dispatch(authActions.authCheckState()),
    setLoading: (val) => dispatch({ type: actionTypes.DATA_LOADING, value: val }),
    getUserAuth: (token) => dispatch(authActions.getAuthUser(token)),
    closeSuccessSB: () => dispatch(notificationActions.closeSuccess()),
    closeErrorSB: () => dispatch(notificationActions.closeError()),
    closeInfoSB: () => dispatch(notificationActions.closeInfo()),
    getPermissionList: () => dispatch(getPermissions()),
    getRoleList: () => dispatch(getRoles()),
    getCompanyList: () => dispatch(getCompanies()),
    getGroupList: () => dispatch(getGroups()),
    getAssetList: () => dispatch(getAssets()),
    getVPNList: () => dispatch(getVPNs()),
    getLocationList: () => dispatch(getLocations()),
    getSubLocationList: () => dispatch(getSubLocations()),
    getSystemList: () => dispatch(getSystems()),
    getUserList: () => dispatch(getUsers()),
    getAlarmList: () => dispatch(getAlarms()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(App);
